import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabaseClient";
import { User, Role, Manager, Team } from "@/types";
import { useToast } from "@/components/ui/use-toast";
import { logUserActivity } from "@/utils/activityLogger";
import { useTenant } from "@/contexts/TenantContext";
import { fetchManagementAccess } from "@/lib/managementAccess";
import { userCreatedToast } from "@/lib/welcomeEmailNotice";

/**
 * SUB-2.6 (FR13, AC2). The people who hold a role assignment in `tenantId`
 * because they are an Agency Admin of its parent organization.
 *
 * Three reads, all of them already permitted by policies that exist:
 *   1. `user_roles` rows in this account carrying `granted_by_parent_tenant_id`;
 *   2. the `users` rows behind them, which `users_select` allows through its
 *      "holds a role in my current tenant" branch rather than by tenant;
 *   3. the parent's name, for the label.
 *
 * Failures are swallowed and return an empty list on purpose. This is an
 * additive annotation on a list that has to render either way, and a tenant with
 * no parent must be visually identical to today.
 */
/**
 * The roles each person holds IN this account, from `user_roles`, keyed by user id.
 *
 * ⚠️ WHY THIS EXISTS, AND WHY THE TABLE WAS WRONG WITHOUT IT. The main query reads
 * `users` with `role_details:roles(id, name)`, which follows the LEGACY
 * `users.role_id`. `fetchExternalGrantHolders` below does read `user_roles`, but only
 * rows carrying `granted_by_parent_tenant_id`, so an ordinary member holding a
 * per-tenant role was never asked about at all. Measured live 2026-08-13, both
 * members of the one sub-account-enabled account disagreed with themselves, in
 * OPPOSITE directions:
 *
 *   carlos+trippertenant@   users.role_id = Admin          user_roles here = Agency Admin
 *   aglasier24@             users.role_id = Agency Admin   user_roles here = Superadmin
 *
 * The table rendered the legacy value for both, so an Agency Admin appeared as an
 * ordinary Admin. That is what made SUB-3.1's entry point look broken: the gate
 * correctly admitted an Agency Admin the screen was labelling an Admin.
 *
 * A failure returns an empty map and logs. The list has to render either way, and an
 * empty map means every row falls back to the legacy column, which is precisely the
 * wrong answer this read exists to correct — so degrading silently would restore the
 * bug invisibly. That is why the log is not optional.
 *
 * ⚠️ AN RPC, NOT A BROWSER READ OF `user_roles`, AND THE FIRST VERSION OF THIS
 * FUNCTION GOT THAT WRONG. It ran
 *
 *   .from('user_roles').select('user_id, roles(name)').eq('tenant_id', tenantId)
 *
 * and shipped, and the column did not change, because that policy is
 * `check_user_role('Admin') OR auth.uid() = user_id` and the predicate is FALSE for
 * a Superadmin (it tests the literal name 'Admin') and false for an Agency Admin.
 * So the read returned the caller's OWN row alone, every other user fell back to the
 * legacy column, and the table went on saying "Admin" — with no error, because an RLS
 * filter is not an error.
 *
 * `list_tenant_role_assignments` (20260813e) carries the gate instead: the caller's
 * CURRENT account only, never its subtree, for a superadmin, an Admin or an Agency
 * Admin of it. Same reasoning as SUB-2.11 replacing a browser read of `tenants` with
 * `list_sub_accounts` because the original only worked through F-13.
 */
interface TenantRoleAssignment {
  user_id?: string;
  role_id?: number | string | null;
  role_name?: string | null;
  granted_by_parent_tenant_id?: string | null;
}

/**
 * One read of who holds what in this account. Both the role column and the
 * external-grant list are derived from it.
 *
 * ⚠️ SUB-3.14. THE EXTERNAL-GRANT LIST USED TO READ `user_roles` FROM THE BROWSER
 * AND THAT MADE THE ACCOUNT LOOK CLEAN. `20260813e`'s own closing note records
 * it: the SELECT policy is `check_user_role('Admin') OR auth.uid() = user_id`,
 * which is FALSE for a Superadmin and for an Agency Admin, so RLS filtered the
 * query down to the caller's own row and returned NO ERROR. The list of outside
 * people holding standing access rendered EMPTY, and an empty list and "nobody
 * has access" are the same picture. The loud error log below could never fire,
 * because an RLS filter is not an error.
 */
const fetchTenantRoleAssignments = async (
  tenantId: string
): Promise<TenantRoleAssignment[] | null> => {
  try {
    const { data, error } = await supabase.rpc("list_tenant_role_assignments", {
      p_tenant_id: tenantId,
    });

    if (error) {
      // A PGRST202 here means 20260813e has not been applied in this environment,
      // which is a deploy-ordering fact worth seeing rather than a silent fallback
      // to the legacy column.
      console.error(
        "[useUsers] Could not read per-tenant role assignments. The role column falls back to the legacy users.role_id, which can understate a user's authority, and outside grant holders will not be listed:",
        error
      );
      return null;
    }

    return (data ?? []) as TenantRoleAssignment[];
  } catch (e) {
    console.error(
      "[useUsers] Unexpected failure reading per-tenant role assignments:",
      e
    );
    return null;
  }
};

const rolesHeldInTenant = (
  assignments: TenantRoleAssignment[] | null
): Map<string, string[]> => {
  const byUser = new Map<string, string[]>();
  for (const row of assignments ?? []) {
    const name = typeof row?.role_name === "string" ? row.role_name.trim() : "";
    if (!row?.user_id || name === "") continue;

    const userId = String(row.user_id);
    byUser.set(userId, [...(byUser.get(userId) ?? []), name]);
  }
  return byUser;
};

/**
 * The same assignments as ids (USR-1.3), because the edit form submits an id while the
 * table renders a name. Kept as a separate pass rather than folded into the one above
 * so the render path kicks out nameless rows and the form path kicks out unusable ids,
 * which are different questions about the same row.
 */
const roleIdsHeldInTenant = (
  assignments: TenantRoleAssignment[] | null
): Map<string, number[]> => {
  const byUser = new Map<string, number[]>();
  for (const row of assignments ?? []) {
    if (!row?.user_id || row.role_id === null || row.role_id === undefined) continue;
    // PostgREST returns bigint as a string, so this is a parse and not a cast.
    const id =
      typeof row.role_id === "number"
        ? row.role_id
        : parseInt(String(row.role_id).trim(), 10);
    if (!Number.isInteger(id) || id <= 0) continue;

    const userId = String(row.user_id);
    byUser.set(userId, [...(byUser.get(userId) ?? []), id]);
  }
  return byUser;
};

const fetchExternalGrantHolders = async (
  assignments: TenantRoleAssignment[] | null,
  alreadyListed: Set<string>
): Promise<User[]> => {
  try {
    // ⚠️ THE ONLY PLACE an account's Admin learns that an outside organization
    // holds standing access to their tenant, so "the query failed" and "nobody
    // has access" must not be the same silent outcome. `null` means the read
    // failed and has already been logged by fetchTenantRoleAssignments; an empty
    // array means the answer really is nobody.
    if (assignments === null) return [];

    const grants = assignments.flatMap((row) =>
      row.user_id && row.granted_by_parent_tenant_id
        ? [
            {
              user_id: String(row.user_id),
              parent_tenant_id: String(row.granted_by_parent_tenant_id),
              role_id: row.role_id,
              role_name: row.role_name,
            },
          ]
        : []
    );
    if (!grants.length) return [];

    // Dedupe by user, not by grant. This iterated GRANTS, so two user_roles rows
    // for one person in one tenant produced two User objects sharing an id —
    // rendered by UserList as two rows with the same React key.
    const seenUserIds = new Set<string>();
    const external = grants.filter((g) => {
      if (alreadyListed.has(g.user_id)) return false;
      if (seenUserIds.has(g.user_id)) return false;
      seenUserIds.add(g.user_id);
      return true;
    });
    if (!external.length) return [];

    const { data: profiles, error: profilesError } = await supabase
      .from("users")
      .select(
        "id, first_name, last_name, email, phone_number, role_id, is_active, created_at, updated_at"
      )
      .in(
        "id",
        external.map((g) => g.user_id)
      )
      // Members of this tenant are filtered on is_active by the main query. An
      // external grant holder was not, so a deactivated Agency Admin stayed
      // listed in every sub-account, badged and unremovable, while a deactivated
      // member of the account disappeared. One rule for both.
      .neq("is_active", false);

    if (profilesError) {
      console.error(
        "[useUsers] Could not read profiles for parent-granted users:",
        profilesError
      );
      return [];
    }

    if (!profiles?.length) return [];

    // The parent's name is a label, not a permission. If it cannot be read the
    // row still says the entry belongs to a parent organization, just unnamed.
    //
    // ⚠️ SUB-3.14. THIS BROWSER READ OF `tenants` ONLY WORKS THROUGH F-13. A
    // client Admin is allowed their OWN tenant row and no other, so the parent's
    // name resolves here only because `tenants_select` still admits any user
    // holding a global legacy role to every row — an open finding. When F-13
    // closes, this read starts returning nothing and the badge silently loses its
    // name. `account_management_access()` answers the same question through a
    // function that is allowed to, so it is the fallback: no behaviour change
    // while F-13 is open, and the name survives the day it closes.
    const parentIds = Array.from(new Set(external.map((g) => g.parent_tenant_id)));
    const { data: parents } = await supabase
      .from("tenants")
      .select("tenant_id, company_name")
      .in("tenant_id", parentIds);

    const parentNames = new Map<string, string>(
      (parents || []).map((p) => [p.tenant_id, p.company_name])
    );

    if (parentIds.some((id) => !parentNames.get(id))) {
      const access = await fetchManagementAccess();
      if (access?.parentCompanyName) {
        // One account has at most one parent (FR6, the one-level rule enforced by
        // `enforce_one_level_hierarchy`), so there is exactly one name to fill in.
        for (const id of parentIds) {
          if (!parentNames.get(id)) parentNames.set(id, access.parentCompanyName);
        }
      }
    }

    return external.flatMap((grant) => {
      const profile = profiles.find((p) => p.id === grant.user_id);
      if (!profile) return [];

      // The role shown is the one they hold HERE, not their role at the parent.
      const parentId = grant.parent_tenant_id;

      return [
        {
          ...profile,
          role_id: profile.role_id ? String(profile.role_id) : undefined,
          role_details: grant.role_name
            ? { id: String(grant.role_id ?? ""), name: String(grant.role_name) }
            : undefined,
          user_teams: [],
          installer_partners: [],
          team_ids: [],
          external_grant: {
            parent_tenant_id: parentId,
            parent_company_name: parentNames.get(parentId),
          },
        } as User,
      ];
    });
  } catch (e) {
    console.error(
      "[useUsers] Unexpected failure reading parent-granted roles. External grants are not shown:",
      e
    );
    return [];
  }
};

export const useUsers = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [deletedUsers, setDeletedUsers] = useState<any[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [managers, setManagers] = useState<Manager[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(false);
  const [deletedUsersLoading, setDeletedUsersLoading] = useState(false);
  const [addUserError, setAddUserError] = useState<string | null>(null);
  const [lastUsersFetch, setLastUsersFetch] = useState<number>(0);
  const [lastDeletedUsersFetch, setLastDeletedUsersFetch] = useState<number>(0);
  const { toast } = useToast();
  const { currentTenant } = useTenant();

  const fetchUsers = useCallback(async () => {
    // Prevent multiple simultaneous calls
    if (loading) {
      return;
    }

    // Rate limiting: don't fetch if we just fetched within the last 1 second
    const now = Date.now();
    if (now - lastUsersFetch < 1000) {
      return;
    }

    try {
      setLoading(true);
      setLastUsersFetch(now);

      // Get current tenant ID from cached context
      const tenantId = currentTenant?.tenant_id;
      if (!tenantId) {
        console.error("No tenant ID available");
        toast({
          title: "Error",
          description: "No tenant ID available.",
          variant: "destructive",
        });
        return;
      }

      const { data: usersData, error: usersError } = await supabase
        .from("users")
        .select(
          `
          id,
          first_name,
          last_name,
          email,
          phone_number,
          role_id,
          manager_id,
          onboarding_required,
          commercial_or_residential,
          is_active,
          created_at,
          updated_at,
          role_details:roles(id, name)
        `
        )
        .eq("tenant_id", tenantId)
        .eq("is_active", true)
        .order("first_name");

      if (usersError) {
        toast({
          title: "Error",
          description: "Failed to load users.",
          variant: "destructive",
        });
        return;
      }

      const { data: userTeamsData, error: userTeamsError } = await supabase
        .from("user_teams")
        .select(
          `
          user_id,
          team_id,
          teams!inner(id, name)
        `
        )
        .in(
          "user_id",
          usersData.map((u) => u.id)
        );

      if (userTeamsError) {
        toast({
          title: "Error",
          description: "Failed to load user teams.",
          variant: "destructive",
        });
        return;
      }

      // Fetch installer partner assignments from junction table
      const {
        data: userInstallerPartnersData,
        error: userInstallerPartnersError,
      } = await supabase.from("user_installer_partners").select(`
          user_id,
          installer_partner_id,
          installer_partners!inner(
            id,
            company_name
          )
        `);

      if (userInstallerPartnersError) {
        toast({
          title: "Error",
          description: "Failed to load user installer partner assignments.",
          variant: "destructive",
        });
        return;
      }

      // SUB-2.6 (FR13, AC2). People who hold a role assignment in THIS account
      // because they are an Agency Admin of its parent organization are not
      // members of this account, so the tenant-filtered query above cannot see
      // them. They still belong in the list, labelled, and the account's own
      // Admin cannot remove the entry (the database refuses it).
      //
      // Nothing here derives the hierarchy: the marked row is the whole source of
      // truth. An account with no parent gets an empty result and the list is
      // byte-identical to what it renders today (ISO8, FR8).
      // One read, two consumers. Both the role column and the external-grant list
      // are answers about `user_roles` in this account, and asking twice was two
      // chances to disagree with each other.
      const assignments = await fetchTenantRoleAssignments(tenantId);

      const externalUsers = await fetchExternalGrantHolders(
        assignments,
        new Set((usersData || []).map((u) => u.id))
      );

      // The role each person holds HERE. Attached to every row, member or external,
      // so the table renders one rule rather than choosing per row. See the comment
      // on fetchTenantRoleAssignments.
      const rolesHere = rolesHeldInTenant(assignments);
      const roleIdsHere = roleIdsHeldInTenant(assignments);

      const usersWithTeams =
        usersData?.map((user) => {
          const userTeams =
            userTeamsData?.filter((ut) => ut.user_id === user.id) || [];
          const userInstallerAssignments =
            userInstallerPartnersData?.filter(
              (uip) => uip.user_id === user.id
            ) || [];

          // Extract installer partner data from the junction table
          const userInstallerPartners = userInstallerAssignments.map(
            (assignment) => {
              const installerPartner = Array.isArray(
                assignment.installer_partners
              )
                ? assignment.installer_partners[0]
                : assignment.installer_partners;
              return {
                id: String(
                  installerPartner?.id || assignment.installer_partner_id
                ),
                company_name:
                  installerPartner?.company_name || "Unknown Company",
              };
            }
          );

          // Combine sales team IDs and installer partner IDs for compatibility
          const salesTeamIds = userTeams.map((ut) => String(ut.team_id));
          const installerPartnerIds = userInstallerPartners.map((ip) =>
            String(ip.id)
          );
          const allTeamIds = [...salesTeamIds, ...installerPartnerIds];

          return {
            ...user,
            roles_in_tenant: rolesHere.get(String(user.id)) ?? [],
            role_ids_in_tenant: roleIdsHere.get(String(user.id)) ?? [],
            user_teams: userTeams.map((ut) => {
              // Handle both single object and array cases from Supabase
              const teamData = Array.isArray(ut.teams) ? ut.teams[0] : ut.teams;
              return {
                team_id: String(ut.team_id),
                teams: {
                  id: String(teamData?.id || ""),
                  name: String(teamData?.name || ""),
                },
              };
            }),
            installer_partners: userInstallerPartners,
            team_ids: allTeamIds,
            // Fix role_details to match User interface
            role_details: (() => {
              if (!user.role_details) return undefined;
              if (
                Array.isArray(user.role_details) &&
                user.role_details.length > 0
              ) {
                const firstRole = user.role_details[0] as any;
                return {
                  id: String(firstRole.id),
                  name: String(firstRole.name),
                };
              }
              if (
                typeof user.role_details === "object" &&
                !Array.isArray(user.role_details)
              ) {
                const roleDetails = user.role_details as any;
                return {
                  id: String(roleDetails.id),
                  name: String(roleDetails.name),
                };
              }
              return undefined;
            })(),
          };
        }) || [];

      // ⚠️ THE SORT IS CONDITIONAL, AND THAT IS THE WHOLE POINT OF IT.
      //
      // This ran unconditionally, which made the "byte-identical to what it
      // renders today (ISO8, FR8)" claim above false for every account on the
      // platform, not just accounts with a parent. The base query is already
      // `.order("first_name")` in the DATABASE collation; re-sorting with
      // localeCompare disagrees with it on case and diacritics, and a NULL
      // first_name moves from last under Postgres ASC to first under
      // `"".localeCompare`. That is the same collation trap tenantHierarchy.ts
      // documents and deliberately avoids.
      //
      // With no external grants there is nothing to merge, so the server's order
      // is kept exactly. Only the merge case needs a client-side ordering, and
      // that case did not exist before this story.
      // External grant holders get the same annotation. Their `role_details` already
      // carries the granted role, but the renderer reads `roles_in_tenant` now, so
      // omitting it here would blank the one column their row exists to explain.
      const externalWithRolesHere = externalUsers.map((u) => ({
        ...u,
        roles_in_tenant: rolesHere.get(String(u.id)) ?? [],
        role_ids_in_tenant: roleIdsHere.get(String(u.id)) ?? [],
      }));

      setUsers(
        externalWithRolesHere.length === 0
          ? usersWithTeams
          : [...usersWithTeams, ...externalWithRolesHere].sort((a, b) =>
              (a.first_name || "").localeCompare(b.first_name || "")
            )
      );
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load users.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [toast, currentTenant?.tenant_id]);

  const fetchDeletedUsers = useCallback(async () => {
    // Prevent multiple simultaneous calls
    if (deletedUsersLoading) {
      return;
    }

    // Rate limiting: don't fetch if we just fetched within the last 1 second
    const now = Date.now();
    if (now - lastDeletedUsersFetch < 1000) {
      return;
    }

    try {
      setDeletedUsersLoading(true);
      setLastDeletedUsersFetch(now);

      // Get current tenant ID from cached context
      const tenantId = currentTenant?.tenant_id;
      if (!tenantId) {
        console.error("No tenant ID available");
        toast({
          title: "Error",
          description: "No tenant ID available.",
          variant: "destructive",
        });
        return;
      }

      const { data: deletedUsersData, error: deletedUsersError } =
        await supabase
          .from("users")
          .select(
            `
          id,
          email,
          first_name,
          last_name,
          deleted_at,
          deleted_by,
          original_email,
          original_first_name,
          original_last_name,
          created_at
        `
          )
          .eq("tenant_id", tenantId)
          .eq("is_active", false)
          .not("deleted_at", "is", null)
          .order("deleted_at", { ascending: false });

      if (deletedUsersError) {
        console.error("Failed to fetch deleted users:", deletedUsersError);
        // Only show toast for non-network errors to prevent spam
        if (!deletedUsersError.message?.includes("INSUFFICIENT_RESOURCES")) {
          toast({
            title: "Error",
            description: "Failed to load deleted users.",
            variant: "destructive",
          });
        }
        return;
      }

      setDeletedUsers(deletedUsersData || []);
    } catch (error: any) {
      console.error("Failed to fetch deleted users:", error);
      // Only show toast for non-network errors to prevent spam
      if (
        !error?.message?.includes("INSUFFICIENT_RESOURCES") &&
        !error?.name?.includes("NetworkError")
      ) {
        toast({
          title: "Error",
          description: "Failed to load deleted users.",
          variant: "destructive",
        });
      }
    } finally {
      setDeletedUsersLoading(false);
    }
  }, [toast, currentTenant?.tenant_id]);

  const fetchRoles = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from("roles")
        .select("*")
        .order("name");

      if (error) {
        toast({
          title: "Error",
          description: "Failed to load roles.",
          variant: "destructive",
        });
        return;
      }
      setRoles(data || []);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load roles.",
        variant: "destructive",
      });
    }
  }, [toast]);

  const fetchManagers = useCallback(async () => {
    try {
      // Query users table instead of non-existent user_managers table
      const { data, error } = await supabase
        .from("users")
        .select("id, first_name, last_name, email, role_id")
        .eq("is_active", true)
        .order("first_name");

      if (error) {
        toast({
          title: "Error",
          description: "Failed to load managers.",
          variant: "destructive",
        });
        return;
      }
      setManagers(data || []);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load managers.",
        variant: "destructive",
      });
    }
  }, [toast]);

  const fetchTeams = useCallback(async () => {
    try {
      // Get current tenant ID
      const { data: tenantId, error: tenantError } = await supabase.rpc(
        "get_current_user_tenant_id"
      );
      if (tenantError) {
        console.error("Error getting tenant ID:", tenantError);
        toast({
          title: "Error",
          description: "Failed to get tenant ID.",
          variant: "destructive",
        });
        return;
      }

      const { data, error } = await supabase
        .from("teams")
        .select(
          "id, name, type, manager_id, biz_dev_id"
        )
        .eq("tenant_id", tenantId)
        .order("name");

      if (error) {
        toast({
          title: "Error",
          description: "Failed to load teams.",
          variant: "destructive",
        });
        return;
      }

      // Transform data to include manager_name and biz_dev_rep_name
      const userIds = new Set<string>();
      data.forEach((team) => {
        if (team.manager_id) userIds.add(team.manager_id);
        if (team.biz_dev_id) userIds.add(team.biz_dev_id);
      });

      const { data: userData, error: userError } = await supabase
        .from("users")
        .select("id, first_name, last_name")
        .in("id", Array.from(userIds));

      if (userError) {
        toast({
          title: "Error",
          description: "Failed to load user details for teams.",
          variant: "destructive",
        });
        return;
      }

      const userMap = new Map<
        string,
        { first_name: string; last_name: string }
      >();
      userData.forEach((user) => {
        userMap.set(user.id, {
          first_name: user.first_name,
          last_name: user.last_name,
        });
      });

      const transformedData = data.map((team) => ({
        ...team,
        manager_name:
          team.manager_id && userMap.get(team.manager_id)
            ? `${userMap.get(team.manager_id)!.first_name} ${userMap.get(team.manager_id)!.last_name}`
            : "N/A",
        biz_dev_rep_name:
          team.biz_dev_id && userMap.get(team.biz_dev_id)
            ? `${userMap.get(team.biz_dev_id)!.first_name} ${userMap.get(team.biz_dev_id)!.last_name}`
            : "N/A",
      }));

      setTeams(transformedData || []);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load teams.",
        variant: "destructive",
      });
    }
  }, [toast, currentTenant?.tenant_id]);

  // Initial data fetch on mount and when tenant changes
  useEffect(() => {
    fetchUsers();
    fetchRoles();
    fetchManagers();
    fetchTeams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTenant?.tenant_id]); // Re-fetch when tenant changes

  interface CreateUserData extends Omit<User, "id" | "team_id"> {
    team_ids?: string[]; // Use team_ids to match formData
    commercial_or_residential?: "residential" | "commercial";
    residentialStates?: string[];
    commercialStates?: string[];
  }

  const createUser = async (userData: CreateUserData) => {
    try {
      const functionsBaseUrl =
        import.meta.env.VITE_SUPABASE_FUNCTIONS_URL || "/functions/v1";

      const sessionResult = await supabase.auth.getSession();
      const session = sessionResult.data.session;

      if (!session) {
        toast({
          title: "Error",
          description: "You must be logged in to perform this action.",
          variant: "destructive",
        });
        return { success: false };
      }

      const payload = {
        first_name: userData.first_name,
        last_name: userData.last_name,
        email: userData.email,
        role_id: userData.role_id,
        phone_number: userData.phone_number || "",
        team_ids: userData.team_ids || [],
        password: userData.password || undefined,
        manager_id: userData.manager_id || null,
        commercial_or_residential:
          userData.commercial_or_residential || "residential",
        require_onboarding: false,
        target_tenant_id: currentTenant?.tenant_id, // Pass the current tenant context
      };

      const response = await fetch(`${functionsBaseUrl}/create-admin-user`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (!response.ok) {
        setAddUserError(data.error || "Failed to create user.");
        toast({
          title: "Error",
          description: data.error || "Failed to create user.",
          variant: "destructive",
        });
        return { success: false };
      }

      // Save allowed states for the new user if they were provided
      if (data.user && currentTenant?.tenant_id) {
        const newUserId = data.user.id;

        // Check if the user has custom states in userData
        if (userData.residentialStates || userData.commercialStates) {
          const defaultResidentialStates = ['AR', 'AZ', 'CT', 'CO', 'IA', 'KS', 'KY', 'LA', 'MA', 'ME', 'MO', 'NC', 'NH', 'NJ', 'OH', 'OK', 'PA', 'RI', 'SC', 'TX', 'VT', 'MD', 'FL'];
          const defaultCommercialStates = ['CT', 'IL', 'MD', 'MA', 'NJ', 'PA', 'RI', 'VA', 'DC', 'WV', 'ME', 'NH', 'VT'];

          const statesToSave = {
            user_id: newUserId,
            tenant_id: currentTenant.tenant_id,
            residential_states: userData.residentialStates || defaultResidentialStates,
            commercial_states: userData.commercialStates || defaultCommercialStates,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          };

          const { error: statesError } = await supabase
            .from('allowed_user_states')
            .insert(statesToSave);

          if (statesError) {
            console.error('Error saving user allowed states:', statesError);
          }
        } else {
          // Insert default states for new user
          const defaultResidentialStates = ['AR', 'AZ', 'CT', 'CO', 'IA', 'KS', 'KY', 'LA', 'MA', 'ME', 'MO', 'NC', 'NH', 'NJ', 'OH', 'OK', 'PA', 'RI', 'SC', 'TX', 'VT', 'MD', 'FL'];
          const defaultCommercialStates = ['CT', 'IL', 'MD', 'MA', 'NJ', 'PA', 'RI', 'VA', 'DC', 'WV', 'ME', 'NH', 'VT'];

          const { error: statesError } = await supabase
            .from('allowed_user_states')
            .insert({
              user_id: newUserId,
              tenant_id: currentTenant.tenant_id,
              residential_states: defaultResidentialStates,
              commercial_states: defaultCommercialStates,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            });

          if (statesError) {
            console.error('Error saving default user allowed states:', statesError);
          }
        }
      }

      await fetchUsers(); // Refresh users
      setAddUserError(null);

      toast(userCreatedToast(data));

      // Log the user creation activity (don't let this break the user creation)
      if (data.user?.id) {
        try {
          await logUserActivity.created(data.user.id, {
            email: userData.email,
            first_name: userData.first_name,
            last_name: userData.last_name,
            role_id: userData.role_id,
          });
        } catch (logError) {
          console.warn("Failed to log user creation activity:", logError);
          // Don't fail the user creation process if logging fails
        }
      }

      return { success: true, user: data.user };
    } catch (error: any) {
      setAddUserError(error?.message || "Failed to create user.");
      toast({
        title: "Error",
        description: error?.message || "Failed to create user.",
        variant: "destructive",
      });
      return { success: false };
    }
  };

  const updateUser = async (user: User) => {
    try {
      const session = await supabase.auth.getSession();
      if (!session.data.session) {
        toast({
          title: "Error",
          description: "You must be logged in to perform this action.",
          variant: "destructive",
        });
        return { success: false };
      }

      const updatePayload = {
        ...user,
        // Remove fields that don't exist in database
        require_onboarding: undefined,
      };

      const functionsBaseUrl =
        import.meta.env.VITE_SUPABASE_FUNCTIONS_URL || "/functions/v1";
      const response = await fetch(`${functionsBaseUrl}/update-admin-user`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.data.session.access_token}`,
        },
        body: JSON.stringify(updatePayload),
      });
      const data = await response.json();

      if (!response.ok) {
        toast({
          title: "Error",
          description: data.error || "Failed to update user.",
          variant: "destructive",
        });
        return { success: false };
      }
      await fetchUsers(); // Refresh users

      toast({
        title: "Success",
        description: "User updated successfully!",
      });

      // Log the user update activity (don't let this break the user update)
      try {
        await logUserActivity.updated(user.id, {
          email: user.email,
          first_name: user.first_name,
          last_name: user.last_name,
          role_id: user.role_id,
        });
      } catch (logError) {
        console.warn("Failed to log user update activity:", logError);
        // Don't fail the user update process if logging fails
      }

      return { success: true };
    } catch (error: any) {
      toast({
        title: "Error",
        description: error?.message || "Failed to update user.",
        variant: "destructive",
      });
      return { success: false };
    }
  };

  const deleteUser = async (userId: string, isHardDelete: boolean = false) => {
    try {
      const session = await supabase.auth.getSession();
      if (!session.data.session) {
        toast({
          title: "Error",
          description: "You must be logged in to perform this action.",
          variant: "destructive",
        });
        return { success: false };
      }
      const functionsBaseUrl =
        import.meta.env.VITE_SUPABASE_FUNCTIONS_URL || "/functions/v1";
      const response = await fetch(`${functionsBaseUrl}/delete-admin-user`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.data.session.access_token}`,
        },
        body: JSON.stringify({
          id: userId,
          hard_delete: isHardDelete,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        toast({
          title: "Error",
          description:
            data.error ||
            `Failed to ${isHardDelete ? "permanently delete" : "delete"} user.`,
          variant: "destructive",
        });
        return { success: false };
      }
      await fetchUsers(); // Refresh users
      if (!isHardDelete) {
        await fetchDeletedUsers(); // Refresh deleted users (only for soft delete)
      }

      // Log the user deletion activity
      await logUserActivity.deleted(userId, {
        deleted_user_id: userId,
        hard_delete: isHardDelete,
      });

      return { success: true };
    } catch (error: any) {
      toast({
        title: "Error",
        description:
          error?.message ||
          `Failed to ${isHardDelete ? "permanently delete" : "delete"} user.`,
        variant: "destructive",
      });
      return { success: false };
    }
  };

  return {
    users,
    deletedUsers,
    loading,
    deletedUsersLoading,
    roles,
    managers,
    teams,
    createUser,
    updateUser,
    deleteUser,
    fetchDeletedUsers,
    fetchUsers,
    fetchTeams,
    addUserError,
  };
};
